import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  Lock,
  PlayCircle,
  X,
} from 'lucide-react';
import useScrollToTop from '../hooks/useScrollToTop';
import {
  useCourseApi,
  CourseDetails,
} from '../hooks/useCourseApi';
import UnitAccessTimer from '../components/common/UnitAccessTimer';
import { cardTheme } from '../styles/cardTheme';
import { mapCourseLoadError } from '../utils/courseLoadLabels';
import { getCurriculumUnitAccessStatus } from '../utils/curriculumUnitAccess';
import ConfiguredPracticeQuiz from '../components/quiz/ConfiguredPracticeQuiz';
import TeacherQaNotice from '../components/course/TeacherQaNotice';
import { shouldRenderTeacherQaNotice } from '../config/teacherQaChannels';

const CourseCurriculum: React.FC = () => {
  useScrollToTop();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { fetchCourseDetails, isLoading } = useCourseApi();
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load once per course. In-flight requests are shared so remounts do not
  // fire extra tree-course-with-progress calls.
  useEffect(() => {
    if (!courseId) return;

    let cancelled = false;

    const loadCourseDetails = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        const response = await fetchCourseDetails(courseId);
        if (cancelled) return;

        if (response?.success) {
          setCourseDetails(response.data);
        } else {
          setError(
            response?.message ||
              (response === null ? 'حدث خطأ أثناء تحميل الدورة' : 'Course details not found.')
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading course:', err);
        setError('حدث خطأ أثناء تحميل الدورة');
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    loadCourseDetails();

    return () => {
      cancelled = true;
    };
  }, [courseId, fetchCourseDetails]);

  const openUnit = (unitIndex: number) => {
    if (!courseDetails || !courseId) return;

    const unitAccess = getCurriculumUnitAccessStatus(
      unitIndex,
      courseDetails.enrollmentDate,
      courseDetails.isOpenToAll
    );
    if (!unitAccess.accessible) {
      setError(unitAccess.message || 'هذه الوحدة غير متاحة حالياً');
      return;
    }

    const unit = courseDetails.units[unitIndex];
    if (!unit?.lessons?.length) {
      setError('لسه مفيش دروس في الوحدة دي');
      return;
    }

    const videoIndex = unit.lessons.findIndex((lesson) => lesson.type === 'Video');
    const lessonIndex = videoIndex >= 0 ? videoIndex : 0;
    const lesson = unit.lessons[lessonIndex];

    setError(null);
    navigate(
      `/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`
    );
  };

  const calculateProgress = () => {
    if (!courseDetails || !courseDetails.units) return 0;
    
    let totalLessons = 0;
    let completedLessons = 0;
    
    courseDetails.units.forEach(unit => {
      unit.lessons.forEach(lesson => {
        totalLessons++;
        if ((lesson.type === 'Video' && lesson.isCompleted) || 
            (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
            (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
            (lesson.type === 'File' && lesson.isFileOpened)) {
          completedLessons++;
        }
      });
    });
    
    return totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  };

  // Show initial loading screen when page is first opened
  if (initialLoading || (isLoading && !courseDetails)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className="w-full max-w-sm p-4 text-center sm:p-8">
          <div className="relative mx-auto mb-5 h-14 w-14 sm:mb-6 sm:h-16 sm:w-16">
            <div className="absolute inset-0 animate-spin rounded-full border-4 border-green-100 border-t-green-700" />
          </div>
          <h3 className={`mb-2 text-lg font-bold sm:text-xl ${cardTheme.title}`}>جاري تحميل المنهج</h3>
          <p className={`text-sm sm:text-base ${cardTheme.subtitle}`}>يرجى الانتظار بينما نحمّل محتوى الدورة...</p>
        </div>
      </div>
    );
  }

  if (error && !courseDetails) {
    const ui = mapCourseLoadError(
      !courseId ? 'missing course id' : error
    );
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}>
            <BookOpen className="h-7 w-7" />
          </div>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto sm:text-base ${cardTheme.btnPrimary}`}
          >
            {ui.buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  if (!courseDetails) {
    const ui = mapCourseLoadError('Course details not found.');
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto sm:text-base ${cardTheme.btnPrimary}`}
          >
            {ui.buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = courseDetails
    ? courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0)
    : 0;
  const progressPct = calculateProgress();

  const availableQuizzes = courseDetails
    ? courseDetails.units.flatMap((unit) =>
        unit.lessons
          .filter((lesson) => String(lesson.type).toLowerCase() === 'quiz')
          .map((lesson) => ({
            id: lesson.id,
            lessonName: lesson.lessonName,
            unitName: unit.unitName,
          }))
      )
    : [];

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper font-cairo" dir="rtl">
      <header className="sticky top-0 z-20 border-b border-green-100/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-4xl items-center px-3 sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-green-800 hover:text-green-600"
          >
            <ArrowRight className="h-5 w-5" />
            كورساتي وحصصي
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-4 pb-24 sm:px-6 sm:py-8 sm:pb-10">
        {courseDetails && (
          <section className={`${cardTheme.shell} mb-5 overflow-hidden sm:mb-7`}>
            <div className={`${cardTheme.headerBanner} px-4 py-5 sm:px-6 sm:py-6`}>
              <div className={`mb-3 inline-flex ${cardTheme.badgeGold} bg-gold-500/20 text-gold-200 border-gold-400/40`}>
                <BookOpen className="h-3.5 w-3.5" />
                منهج الدورة
              </div>
              <h1 className="mb-2 break-words text-xl font-bold leading-snug text-white sm:text-2xl lg:text-3xl">
                {courseDetails.courseName}
              </h1>
              <p className="mb-4 text-sm text-green-100/85 sm:text-base">
                اختر الوحدة لمشاهدة الدرس — بدّل بين الدروس من التبويبات
              </p>

              <div className="mb-3 h-2.5 overflow-hidden rounded-full bg-white/15">
                <div
                  className="h-full rounded-full bg-gold-400 transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white sm:text-xs">
                  <BookOpen className="h-3.5 w-3.5" />
                  {courseDetails.units.length} وحدات
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white sm:text-xs">
                  <PlayCircle className="h-3.5 w-3.5" />
                  {totalLessons} دروس
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-500/25 px-2.5 py-1 text-[11px] font-semibold text-gold-200 sm:text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {progressPct.toFixed(0)}% مكتمل
                </span>
              </div>
            </div>
          </section>
        )}

        {courseId && courseDetails && shouldRenderTeacherQaNotice(courseDetails) && (
          <TeacherQaNotice courseId={courseId} coursePayload={courseDetails} />
        )}

        {courseDetails && (
          <ConfiguredPracticeQuiz quizzes={availableQuizzes} />
        )}

        {courseDetails && !courseDetails.isOpenToAll && (
          <div className="mb-5 sm:mb-7">
            {courseDetails.units.map((unit, unitIndex) => {
              const unitAccess = getCurriculumUnitAccessStatus(
                unitIndex,
                courseDetails.enrollmentDate,
                courseDetails.isOpenToAll
              );
              if (unitAccess.accessible && unitAccess.daysLeft) {
                return (
                  <UnitAccessTimer
                    key={unit.id}
                    unitIndex={unitIndex}
                    enrollmentDate={courseDetails.enrollmentDate}
                    totalUnits={courseDetails.units.length}
                    className="mb-3 sm:mb-4"
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 sm:mb-5">
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center"
              aria-label="إغلاق"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {courseDetails && (
          <div className="space-y-3 sm:space-y-4">
            <div className="mb-1 flex items-center justify-between gap-2 px-0.5">
              <h2 className={`text-base font-bold sm:text-lg ${cardTheme.title}`}>محتوى الوحدات</h2>
              <span className={cardTheme.badge}>{courseDetails.units.length} وحدة</span>
            </div>

            {courseDetails.units.map((unit, unitIndex) => {
              const unitAccess = getCurriculumUnitAccessStatus(
                unitIndex,
                courseDetails.enrollmentDate,
                courseDetails.isOpenToAll
              );
              const isUnitLocked = !unitAccess.accessible;
              const completedInUnit = unit.lessons.filter(
                (lesson) =>
                  (lesson.type === 'Video' && lesson.isCompleted) ||
                  (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
                  (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
                  (lesson.type === 'File' && lesson.isFileOpened)
              ).length;

              return (
                <div
                  key={unit.id}
                  className={`${cardTheme.shell} ${!isUnitLocked ? cardTheme.shellHover : 'opacity-70'}`}
                >
                  <button
                    type="button"
                    className={`flex w-full min-h-14 items-start gap-3 p-3.5 text-right sm:items-center sm:gap-4 sm:p-5 ${
                      isUnitLocked ? 'cursor-not-allowed bg-gray-50/80' : ''
                    }`}
                    onClick={() => openUnit(unitIndex)}
                    disabled={isUnitLocked}
                  >
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${
                        isUnitLocked ? 'bg-gray-200 text-gray-500' : cardTheme.iconBox
                      }`}
                    >
                      {isUnitLocked ? (
                        <Lock className="h-5 w-5" />
                      ) : (
                        <span className="text-base font-bold sm:text-lg">{unitIndex + 1}</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3
                        className={`mb-1 break-words text-sm font-bold leading-snug sm:text-base lg:text-lg ${
                          isUnitLocked ? 'text-gray-500' : cardTheme.title
                        }`}
                      >
                        {unit.unitName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] font-semibold sm:text-xs ${unitAccess.statusColor}`}>
                          {unitAccess.statusText}
                        </span>
                        <span className="text-[11px] text-gray-500 sm:text-xs">
                          {completedInUnit}/{unit.lessons.length} دروس
                        </span>
                      </div>
                      {!isUnitLocked && unit.lessons.length > 0 && (
                        <div className="mt-2 h-1.5 max-w-[12rem] overflow-hidden rounded-full bg-green-100">
                          <div
                            className="h-full rounded-full bg-gold-500"
                            style={{
                              width: `${(completedInUnit / unit.lessons.length) * 100}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {!isUnitLocked && (
                      <ChevronLeft className="mt-1 h-5 w-5 shrink-0 text-green-700 sm:mt-0" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseCurriculum;
